import { useEffect, useState } from 'react';
import { MapPin } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useDebounce } from '@/hooks/useDebounce';
import { getLocationSuggestions } from '@/services/api';

// Structured property address entry with a lightweight city-name typeahead
// (reuses the same geo/autocomplete + Florida-cities fallback as the map search).
export function PropertyAddressAutocomplete({ value, onChange }) {
  const [citySuggestions, setCitySuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const debouncedCity = useDebounce(value.city, 250);

  useEffect(() => {
    if (!debouncedCity || debouncedCity.length < 2) { setCitySuggestions([]); return; }
    let cancelled = false;
    getLocationSuggestions(debouncedCity).then((results) => {
      if (!cancelled) setCitySuggestions((results || []).filter((r) => r.type === 'city').slice(0, 5));
    });
    return () => { cancelled = true; };
  }, [debouncedCity]);

  const set = (patch) => onChange({ ...value, ...patch });

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <div className="sm:col-span-2">
        <Label>Street address</Label>
        <div className="relative mt-1.5">
          <MapPin size={16} className="absolute left-3 top-3 text-slate-400" />
          <Input
            value={value.address}
            onChange={(e) => set({ address: e.target.value })}
            placeholder="123 Maple St"
            className="rounded-xl pl-9"
            data-testid="sellers-address-street-input"
          />
        </div>
      </div>
      <div className="relative">
        <Label>City</Label>
        <Input
          value={value.city}
          onChange={(e) => { set({ city: e.target.value }); setShowSuggestions(true); }}
          onFocus={() => setShowSuggestions(true)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
          className="mt-1.5 rounded-xl"
          data-testid="sellers-address-city-input"
          autoComplete="off"
        />
        {showSuggestions && citySuggestions.length > 0 && (
          <ul className="absolute left-0 right-0 top-full z-10 mt-1 max-h-48 overflow-auto rounded-xl border border-slate-200 bg-white shadow-lg">
            {citySuggestions.map((s) => (
              <li
                key={s.value}
                onMouseDown={() => { set({ city: (s.label || '').split(',')[0] }); setShowSuggestions(false); }}
                className="cursor-pointer px-3 py-2 text-sm text-slate-700 hover:bg-blue-50"
              >
                {s.label}
              </li>
            ))}
          </ul>
        )}
      </div>
      <div>
        <Label>State</Label>
        <Input value={value.state} onChange={(e) => set({ state: e.target.value.toUpperCase() })} placeholder="FL" maxLength={2} className="mt-1.5 rounded-xl uppercase" data-testid="sellers-address-state-input" />
      </div>
      <div className="sm:col-span-2">
        <Label>ZIP code</Label>
        <Input value={value.zip} onChange={(e) => set({ zip: e.target.value })} placeholder="33498" className="mt-1.5 rounded-xl" data-testid="sellers-address-zip-input" />
      </div>
    </div>
  );
}
